import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import SpinnerOverlay from '../../components/ui/SpinnerOverlay';

const RentalBookingCheckoutScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const params = new URLSearchParams(location?.search);
  const vehicleId = params?.get('vehicleId');
  const rentalDays = parseInt(params?.get('days') || '3');
  const extrasParam = params?.get('extras');
  const extras = extrasParam ? JSON.parse(extrasParam) : {};

  const [vehicle, setVehicle] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [bookingRef, setBookingRef] = useState(null);
  const [cashConfirmed, setCashConfirmed] = useState(false);
  const [form, setForm] = useState({
    pickupLocation: '',
    dropoffLocation: '',
    startDate: '',
    startTime: '09:00',
    endDate: '',
    endTime: '09:00',
    paymentMethod: 'cash',
    notes: '',
  });
  const [docFile, setDocFile] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (vehicleId) fetchVehicle();
  }, [vehicleId]);

  // Auto-compute end date when startDate or rentalDays changes
  useEffect(() => {
    if (form?.startDate && rentalDays) {
      const start = new Date(form?.startDate);
      start?.setDate(start?.getDate() + rentalDays);
      const yyyy = start?.getFullYear();
      const mm = String(start?.getMonth() + 1)?.padStart(2, '0');
      const dd = String(start?.getDate())?.padStart(2, '0');
      setForm(f => ({ ...f, endDate: `${yyyy}-${mm}-${dd}` }));
    }
  }, [form?.startDate, rentalDays]);

  const fetchVehicle = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('vehicles')?.select(`*, vehicle_pricing(*), vehicle_media(*)`)?.eq('id', vehicleId)?.single();
      if (!error && data) {
        setVehicle(data);
        setPricing(data?.vehicle_pricing?.[0]);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const calcBreakdown = () => {
    if (!pricing) return { base: 0, chauffeur: 0, extrasCost: 0, platformFee: 0, deposit: 0, total: 0 };
    const base = (pricing?.base_daily_rate || 0) * rentalDays;
    const chauffeur = extras?.chauffeur ? (pricing?.chauffeur_daily_addon || 0) * rentalDays : 0;
    const extrasCost = (extras?.childSeat ? 500 : 0) + (extras?.insurance ? 1500 * rentalDays : 0) + (extras?.delivery ? 1000 : 0);
    const platformFee = Math.round((base + chauffeur) * ((pricing?.platform_fee_pct || 10) / 100));
    const deposit = pricing?.deposit_amount || 0;
    const total = base + chauffeur + extrasCost + platformFee;
    return { base, chauffeur, extrasCost, platformFee, deposit, total };
  };

  const breakdown = calcBreakdown();

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr)?.toLocaleDateString('en-KE', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };

  const validate = () => {
    const errs = {};
    if (!form?.pickupLocation?.trim()) errs.pickupLocation = 'Pickup location required';
    if (!form?.startDate) errs.startDate = 'Start date required';
    if (!form?.endDate) errs.endDate = 'End date required';
    if (form?.paymentMethod === 'cash' && !cashConfirmed) errs.cashConfirmed = 'Please confirm you understand the cash payment terms';
    setErrors(errs);
    return Object.keys(errs)?.length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    if (!user) { navigate('/customer-login-screen'); return; }
    setSubmitting(true);
    try {
      const startAt = `${form?.startDate}T${form?.startTime}:00`;
      const endAt = `${form?.endDate}T${form?.endTime}:00`;

      let docPath = null;
      if (docFile) {
        const { data: uploadData } = await supabase?.storage?.from('vehicle-media')?.upload(`docs/${user?.id}/${Date.now()}_${docFile?.name}`, docFile);
        docPath = uploadData?.path;
      }

      const priceBreakdown = { ...breakdown, rentalDays, extras, docPath, paymentMethod: form?.paymentMethod };
      const rentalMode = extras?.chauffeur ? 'chauffeur' : (vehicle?.rental_mode === 'chauffeur' ? 'chauffeur' : 'self_drive');

      const { data, error } = await supabase?.rpc('create_rental_booking', {
        p_customer_id: user?.id,
        p_vehicle_id: vehicleId,
        p_start_at: startAt,
        p_end_at: endAt,
        p_pickup_location: form?.pickupLocation,
        p_dropoff_location: form?.dropoffLocation || form?.pickupLocation,
        p_rental_mode: rentalMode,
        p_extras: extras,
        p_price_breakdown: priceBreakdown,
      });

      if (error) throw error;
      if (data?.success) {
        setBookingRef(data?.booking_id || data?.id || 'OTG-' + Date.now()?.toString(36)?.toUpperCase());
        setShowSuccessModal(true);
      } else {
        throw new Error(data?.error || 'Booking failed');
      }
    } catch (e) {
      setErrors({ submit: e?.message || 'Booking failed. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a1628' }}>
      <div className="w-10 h-10 border-2 border-[#ffd700] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: '#0a1628', fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');`}</style>
      {submitting && <SpinnerOverlay message="Confirming your booking..." />}

      {/* ── Cash Booking Success Modal ── */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(12px)' }}>
          <div className="w-full max-w-md rounded-3xl p-8 text-center" style={{ background: 'linear-gradient(145deg, #0f2040, #0a1628)', border: '1px solid rgba(255,215,0,0.25)' }}>
            {/* Success icon */}
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: 'rgba(255,215,0,0.12)', border: '2px solid rgba(255,215,0,0.35)' }}>
              <Icon name="CheckCircle" size={40} className="text-[#ffd700]" />
            </div>
            <h2 className="text-white font-black text-2xl mb-2">Booking Submitted!</h2>
            <p className="text-white/50 text-sm mb-6">Your rental request has been received and is pending host confirmation.</p>

            {/* Booking reference */}
            <div className="rounded-2xl p-4 mb-6" style={{ background: 'rgba(255,215,0,0.07)', border: '1px solid rgba(255,215,0,0.2)' }}>
              <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Booking Reference</p>
              <p className="text-[#ffd700] font-black text-xl tracking-widest">{bookingRef}</p>
            </div>

            {/* Cash payment instructions */}
            <div className="rounded-2xl p-5 mb-6 text-left space-y-3" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xl">💵</span>
                <h3 className="text-white font-bold text-sm">Cash Payment Instructions</h3>
              </div>
              {[
                { step: '1', text: 'Await host confirmation (usually within 2 hours)' },
                { step: '2', text: `Prepare KES ${breakdown?.total?.toLocaleString()} in cash for pickup` },
                { step: '3', text: `Also bring KES ${breakdown?.deposit?.toLocaleString()} refundable deposit` },
                { step: '4', text: 'Pay the host directly at vehicle pickup' },
                { step: '5', text: 'Request a receipt from the host upon payment' },
              ]?.map(item => (
                <div key={item?.step} className="flex items-start gap-3">
                  <span className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5" style={{ background: 'rgba(255,215,0,0.15)', color: '#ffd700' }}>{item?.step}</span>
                  <p className="text-white/60 text-xs leading-relaxed">{item?.text}</p>
                </div>
              ))}
            </div>

            {/* Amount summary */}
            <div className="rounded-xl p-4 mb-6 flex justify-between items-center" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div className="text-left">
                <p className="text-white/40 text-xs">Total to Pay at Pickup</p>
                <p className="text-white font-black text-xl">KES {(breakdown?.total + breakdown?.deposit)?.toLocaleString()}</p>
                <p className="text-white/30 text-xs">incl. KES {breakdown?.deposit?.toLocaleString()} deposit</p>
              </div>
              <div className="text-right">
                <p className="text-white/40 text-xs">Rental Period</p>
                <p className="text-white font-bold text-sm">{rentalDays} day{rentalDays !== 1 ? 's' : ''}</p>
                <p className="text-white/30 text-xs">{formatDate(form?.startDate)}</p>
              </div>
            </div>

            <button onClick={() => navigate('/my-rentals')} className="w-full py-4 rounded-2xl font-black text-base transition-all hover:opacity-90" style={{ background: 'linear-gradient(135deg, #ffd700, #f59e0b)', color: '#0a1628' }}>
              View My Rentals
            </button>
            <button onClick={() => navigate('/')} className="w-full py-3 mt-3 rounded-2xl font-semibold text-sm text-white/50 hover:text-white transition-colors">
              Back to Home
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="sticky top-0 z-50 border-b border-white/10" style={{ background: 'rgba(10,22,40,0.95)', backdropFilter: 'blur(20px)' }}>
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded-xl hover:bg-white/10 transition-colors">
            <Icon name="ArrowLeft" size={20} className="text-white" />
          </button>
          <h1 className="text-white font-bold text-lg">Complete Booking</h1>
          <div className="ml-auto flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: 'rgba(255,215,0,0.1)', border: '1px solid rgba(255,215,0,0.2)' }}>
            <span className="text-sm">💵</span>
            <span className="text-[#ffd700] text-xs font-bold">Cash Payment</span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* ── Left: Form ── */}
          <div className="lg:col-span-3 space-y-6">

            {/* Vehicle Summary */}
            {vehicle && (
              <div className="rounded-2xl p-4 flex items-center gap-4" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <div className="w-16 h-12 rounded-xl bg-white/10 flex items-center justify-center text-2xl flex-shrink-0">🚗</div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-bold text-sm truncate">{vehicle?.title}</h3>
                  <p className="text-white/40 text-xs">{vehicle?.make} · {vehicle?.model} · {vehicle?.year}</p>
                  <p className="text-white/30 text-xs mt-0.5">{rentalDays} day{rentalDays !== 1 ? 's' : ''} rental</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-[#ffd700] font-black text-lg">KES {pricing?.base_daily_rate?.toLocaleString()}</div>
                  <div className="text-white/40 text-xs">/day</div>
                </div>
              </div>
            )}

            {/* Rental Dates */}
            <div className="rounded-2xl p-6 space-y-4" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <h2 className="text-white font-bold text-base flex items-center gap-2">
                <Icon name="Calendar" size={16} className="text-[#ffd700]" />
                Rental Dates
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-2 block">Pickup Date *</label>
                  <input type="date" value={form?.startDate} onChange={e => setForm(f => ({ ...f, startDate: e?.target?.value }))}
                    className="w-full px-4 py-3 rounded-xl text-white text-sm outline-none focus:ring-2 focus:ring-[#ffd700]/40"
                    style={{ background: 'rgba(255,255,255,0.07)', border: `1px solid ${errors?.startDate ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.12)'}`, colorScheme: 'dark' }} />
                  {errors?.startDate && <p className="text-red-400 text-xs mt-1">{errors?.startDate}</p>}
                </div>
                <div>
                  <label className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-2 block">Pickup Time</label>
                  <input type="time" value={form?.startTime} onChange={e => setForm(f => ({ ...f, startTime: e?.target?.value }))}
                    className="w-full px-4 py-3 rounded-xl text-white text-sm outline-none focus:ring-2 focus:ring-[#ffd700]/40"
                    style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', colorScheme: 'dark' }} />
                </div>
                <div>
                  <label className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-2 block">Return Date *</label>
                  <input type="date" value={form?.endDate} onChange={e => setForm(f => ({ ...f, endDate: e?.target?.value }))}
                    className="w-full px-4 py-3 rounded-xl text-white text-sm outline-none focus:ring-2 focus:ring-[#ffd700]/40"
                    style={{ background: 'rgba(255,255,255,0.07)', border: `1px solid ${errors?.endDate ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.12)'}`, colorScheme: 'dark' }} />
                  {errors?.endDate && <p className="text-red-400 text-xs mt-1">{errors?.endDate}</p>}
                </div>
                <div>
                  <label className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-2 block">Return Time</label>
                  <input type="time" value={form?.endTime} onChange={e => setForm(f => ({ ...f, endTime: e?.target?.value }))}
                    className="w-full px-4 py-3 rounded-xl text-white text-sm outline-none focus:ring-2 focus:ring-[#ffd700]/40"
                    style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', colorScheme: 'dark' }} />
                </div>
              </div>
              {/* Date range summary */}
              {form?.startDate && form?.endDate && (
                <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background: 'rgba(255,215,0,0.06)', border: '1px solid rgba(255,215,0,0.15)' }}>
                  <Icon name="CalendarCheck" size={16} className="text-[#ffd700] flex-shrink-0" />
                  <p className="text-[#ffd700] text-xs font-medium">
                    {formatDate(form?.startDate)} → {formatDate(form?.endDate)} · <span className="font-black">{rentalDays} day{rentalDays !== 1 ? 's' : ''}</span>
                  </p>
                </div>
              )}
            </div>

            {/* Pickup & Return Location */}
            <div className="rounded-2xl p-6 space-y-4" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <h2 className="text-white font-bold text-base flex items-center gap-2">
                <Icon name="MapPin" size={16} className="text-[#ffd700]" />
                Pickup & Return Location
              </h2>
              <div>
                <label className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-2 block">Pickup Location *</label>
                <input type="text" placeholder="Enter pickup address" value={form?.pickupLocation} onChange={e => setForm(f => ({ ...f, pickupLocation: e?.target?.value }))}
                  className="w-full px-4 py-3 rounded-xl text-white placeholder-white/30 text-sm outline-none focus:ring-2 focus:ring-[#ffd700]/40"
                  style={{ background: 'rgba(255,255,255,0.07)', border: `1px solid ${errors?.pickupLocation ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.12)'}` }} />
                {errors?.pickupLocation && <p className="text-red-400 text-xs mt-1">{errors?.pickupLocation}</p>}
              </div>
              <div>
                <label className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-2 block">Return Location</label>
                <input type="text" placeholder="Same as pickup if empty" value={form?.dropoffLocation} onChange={e => setForm(f => ({ ...f, dropoffLocation: e?.target?.value }))}
                  className="w-full px-4 py-3 rounded-xl text-white placeholder-white/30 text-sm outline-none focus:ring-2 focus:ring-[#ffd700]/40"
                  style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }} />
              </div>
            </div>

            {/* Document Upload */}
            <div className="rounded-2xl p-6 space-y-4" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <h2 className="text-white font-bold text-base flex items-center gap-2">
                <Icon name="FileText" size={16} className="text-[#ffd700]" />
                Documents
              </h2>
              <p className="text-white/40 text-sm">Upload your driving license or national ID for verification</p>
              <label className="flex flex-col items-center justify-center p-6 rounded-xl cursor-pointer hover:bg-white/5 transition-colors" style={{ border: '2px dashed rgba(255,255,255,0.15)' }}>
                <Icon name="Upload" size={24} className="text-white/40 mb-2" />
                <span className="text-white/60 text-sm">{docFile ? docFile?.name : 'Click to upload license/ID'}</span>
                <span className="text-white/30 text-xs mt-1">JPG, PNG, PDF up to 5MB</span>
                <input type="file" accept="image/*,.pdf" className="hidden" onChange={e => setDocFile(e?.target?.files?.[0])} />
              </label>
            </div>

            {/* Payment Method */}
            <div className="rounded-2xl p-6 space-y-4" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <h2 className="text-white font-bold text-base flex items-center gap-2">
                <Icon name="CreditCard" size={16} className="text-[#ffd700]" />
                Payment Method
              </h2>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { value: 'cash', label: 'Cash', icon: '💵' },
                  { value: 'wallet', label: 'Wallet', icon: '👛' },
                  { value: 'card', label: 'Card', icon: '💳' },
                ]?.map(method => (
                  <button key={method?.value} onClick={() => setForm(f => ({ ...f, paymentMethod: method?.value }))}
                    className="p-4 rounded-xl text-center transition-all"
                    style={{ border: `2px solid ${form?.paymentMethod === method?.value ? 'rgba(255,215,0,0.5)' : 'rgba(255,255,255,0.1)'}`, background: form?.paymentMethod === method?.value ? 'rgba(255,215,0,0.08)' : 'transparent' }}>
                    <div className="text-2xl mb-1">{method?.icon}</div>
                    <div className={`text-sm font-bold ${form?.paymentMethod === method?.value ? 'text-[#ffd700]' : 'text-white/60'}`}>{method?.label}</div>
                  </button>
                ))}
              </div>

              {/* Cash payment details panel */}
              {form?.paymentMethod === 'cash' && (
                <div className="space-y-4 mt-2">
                  <div className="rounded-2xl p-5 space-y-3" style={{ background: 'rgba(255,215,0,0.05)', border: '1px solid rgba(255,215,0,0.2)' }}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">💵</span>
                      <h3 className="text-[#ffd700] font-bold text-sm">Cash Payment Details</h3>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center py-2 border-b border-white/5">
                        <span className="text-white/50 text-xs">Rental Amount</span>
                        <span className="text-white font-bold">KES {breakdown?.total?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-white/5">
                        <span className="text-white/50 text-xs">Refundable Deposit</span>
                        <span className="text-white font-bold">KES {breakdown?.deposit?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-white font-bold text-xs">Total Cash at Pickup</span>
                        <span className="text-[#ffd700] font-black text-lg">KES {(breakdown?.total + breakdown?.deposit)?.toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="rounded-xl p-3 mt-2" style={{ background: 'rgba(255,255,255,0.04)' }}>
                      <p className="text-white/40 text-xs leading-relaxed">
                        💡 Cash is paid directly to the host at vehicle pickup. The deposit is fully refunded upon successful return of the vehicle in its original condition.
                      </p>
                    </div>
                  </div>

                  {/* Cash confirmation checkbox */}
                  <label className={`flex items-start gap-3 p-4 rounded-xl cursor-pointer transition-all ${errors?.cashConfirmed ? 'ring-1 ring-red-500/50' : ''}`}
                    style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${cashConfirmed ? 'rgba(255,215,0,0.3)' : 'rgba(255,255,255,0.1)'}` }}>
                    <div className="flex-shrink-0 mt-0.5">
                      <div onClick={() => { setCashConfirmed(c => !c); setErrors(e => ({ ...e, cashConfirmed: undefined })); }}
                        className="w-5 h-5 rounded flex items-center justify-center transition-all cursor-pointer"
                        style={{ background: cashConfirmed ? 'linear-gradient(135deg, #ffd700, #f59e0b)' : 'rgba(255,255,255,0.1)', border: cashConfirmed ? 'none' : '1px solid rgba(255,255,255,0.2)' }}>
                        {cashConfirmed && <Icon name="Check" size={12} className="text-[#0a1628]" />}
                      </div>
                    </div>
                    <p className="text-white/60 text-xs leading-relaxed select-none">
                      I understand that payment of <strong className="text-white">KES {(breakdown?.total + breakdown?.deposit)?.toLocaleString()}</strong> (rental + deposit) will be made in cash directly to the host at the time of vehicle pickup. I agree to the rental terms and conditions.
                    </p>
                  </label>
                  {errors?.cashConfirmed && <p className="text-red-400 text-xs -mt-2">{errors?.cashConfirmed}</p>}
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <label className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-2 block">Special Requests (Optional)</label>
              <textarea rows={3} placeholder="Any special requests or notes..." value={form?.notes} onChange={e => setForm(f => ({ ...f, notes: e?.target?.value }))}
                className="w-full px-4 py-3 rounded-xl text-white placeholder-white/30 text-sm outline-none resize-none focus:ring-2 focus:ring-[#ffd700]/40"
                style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }} />
            </div>

            {errors?.submit && (
              <div className="p-4 rounded-xl" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)' }}>
                <p className="text-red-400 text-sm">{errors?.submit}</p>
              </div>
            )}
          </div>

          {/* ── Right: Summary ── */}
          <div className="lg:col-span-2">
            <div className="sticky top-24 rounded-2xl p-6 space-y-4" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <h2 className="text-white font-bold text-lg">Booking Summary</h2>

              {/* Rental period */}
              {form?.startDate && (
                <div className="rounded-xl p-3 space-y-1" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="Calendar" size={14} className="text-[#ffd700]" />
                    <span className="text-white/60 text-xs font-semibold uppercase tracking-wider">Rental Period</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <div className="flex-1">
                      <p className="text-white/40 text-xs">Pickup</p>
                      <p className="text-white font-semibold text-xs">{formatDate(form?.startDate)}</p>
                      <p className="text-white/40 text-xs">{form?.startTime}</p>
                    </div>
                    <Icon name="ArrowRight" size={14} className="text-white/20 flex-shrink-0" />
                    <div className="flex-1 text-right">
                      <p className="text-white/40 text-xs">Return</p>
                      <p className="text-white font-semibold text-xs">{form?.endDate ? formatDate(form?.endDate) : '—'}</p>
                      <p className="text-white/40 text-xs">{form?.endTime}</p>
                    </div>
                  </div>
                  <div className="text-center mt-2">
                    <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ background: 'rgba(255,215,0,0.12)', color: '#ffd700' }}>
                      {rentalDays} day{rentalDays !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              )}

              {/* Cost breakdown */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-white/50">Base ({rentalDays} day{rentalDays !== 1 ? 's' : ''})</span>
                  <span className="text-white">KES {breakdown?.base?.toLocaleString()}</span>
                </div>
                {breakdown?.chauffeur > 0 && (
                  <div className="flex justify-between">
                    <span className="text-white/50">Chauffeur</span>
                    <span className="text-white">KES {breakdown?.chauffeur?.toLocaleString()}</span>
                  </div>
                )}
                {breakdown?.extrasCost > 0 && (
                  <div className="flex justify-between">
                    <span className="text-white/50">Extras</span>
                    <span className="text-white">KES {breakdown?.extrasCost?.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-white/50">Platform Fee</span>
                  <span className="text-white">KES {breakdown?.platformFee?.toLocaleString()}</span>
                </div>
                <div className="border-t border-white/10 pt-2 flex justify-between font-bold">
                  <span className="text-white">Rental Total</span>
                  <span className="text-[#ffd700] text-lg">KES {breakdown?.total?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-xs pt-1">
                  <span className="text-white/40">+ Refundable Deposit</span>
                  <span className="text-white/60">KES {breakdown?.deposit?.toLocaleString()}</span>
                </div>
                {form?.paymentMethod === 'cash' && (
                  <div className="border-t border-white/10 pt-2 flex justify-between font-black">
                    <span className="text-white text-sm">Cash at Pickup</span>
                    <span className="text-[#ffd700] text-xl">KES {(breakdown?.total + breakdown?.deposit)?.toLocaleString()}</span>
                  </div>
                )}
              </div>

              {/* Payment method badge */}
              <div className="rounded-xl p-3 flex items-center gap-3" style={{ background: 'rgba(255,215,0,0.06)', border: '1px solid rgba(255,215,0,0.15)' }}>
                <span className="text-xl">💵</span>
                <div>
                  <p className="text-[#ffd700] text-xs font-bold">Cash Payment</p>
                  <p className="text-white/40 text-xs">Pay host directly at pickup</p>
                </div>
              </div>

              <div className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <p className="text-white/30 text-xs">💡 Deposit is held and refunded after successful return</p>
              </div>

              <button
                onClick={handleSubmit}
                disabled={submitting || (form?.paymentMethod === 'cash' && !cashConfirmed)}
                className="w-full py-4 rounded-2xl font-black text-base transition-all"
                style={{
                  background: (submitting || (form?.paymentMethod === 'cash' && !cashConfirmed))
                    ? 'rgba(255,215,0,0.3)' :'linear-gradient(135deg, #ffd700, #f59e0b)',
                  color: '#0a1628',
                  cursor: (submitting || (form?.paymentMethod === 'cash' && !cashConfirmed)) ? 'not-allowed' : 'pointer',
                }}>
                {submitting ? 'Processing...' : '✓ Confirm Cash Booking'}
              </button>
              <p className="text-white/30 text-xs text-center">By confirming you agree to the rental terms and conditions</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RentalBookingCheckoutScreen;
